<script setup>
import { computed, onMounted, onUnmounted, provide, ref, watch } from 'vue';
import ConversationApi from 'dashboard/api/conversations';
import { useBreakpoints } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useChatListKeyboardEvents } from 'dashboard/composables/chatlist/useChatListKeyboardEvents';
import wootConstants from 'dashboard/constants/globals';
import ConversationItem from './ConversationItem.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  assigneeType: { type: String, default: 'me' },
  statuses: { type: Array, default: () => ['open'] },
  inboxId: { type: [String, Number], default: 0 },
  teamId: { type: [String, Number], default: 0 },
  label: { type: String, default: '' },
  showAssignee: { type: Boolean, default: false },
  isOnExpandedLayout: { type: Boolean, default: false },
  refreshKey: { type: Array, default: () => [] },
});
const emit = defineEmits(['loaded']);

const { t } = useI18n();
const stageNames = ['unanswered', 'active', 'waiting'];
const stageColors = {
  unanswered: {
    background: 'bg-n-ruby-3',
    marker: 'bg-n-ruby-9',
    text: 'text-n-ruby-11',
  },
  active: {
    background: 'bg-n-blue-3',
    marker: 'bg-n-blue-9',
    text: 'text-n-blue-11',
  },
  waiting: {
    background: 'bg-n-amber-3',
    marker: 'bg-n-amber-9',
    text: 'text-n-amber-11',
  },
};
const stageLabels = computed(() => ({
  unanswered: t('CHAT_LIST.WORK_QUEUE.unanswered'),
  active: t('CHAT_LIST.WORK_QUEUE.active'),
  waiting: t('CHAT_LIST.WORK_QUEUE.waiting'),
}));
const groups = ref({});
const loading = ref(false);
const loadingMore = ref({});
const listRef = ref(null);
const isContextMenuOpen = ref(false);
const breakpoints = useBreakpoints({
  lg: wootConstants.LARGE_SCREEN_BREAKPOINT,
});
const isLgScreen = breakpoints.greaterOrEqual('lg');
const showExpandedCards = computed(
  () => props.isOnExpandedLayout && isLgScreen.value
);
let refreshTimer;
let scheduledRefreshTimer;
let lastRefreshAt = 0;
let pendingRefresh = false;
let isMounted = false;
let requestId = 0;
const MIN_REFRESH_INTERVAL = 10000;
provide('toggleContextMenu', state => {
  isContextMenuOpen.value = state;
});
provide(
  'contextMenuElementTarget',
  computed(() => ({ $el: listRef.value }))
);
useChatListKeyboardEvents(listRef);

const queryParams = computed(() => ({
  assignee_type: props.assigneeType,
  statuses: props.statuses,
  ...(props.inboxId ? { inbox_id: props.inboxId } : {}),
  ...(props.teamId ? { team_id: props.teamId } : {}),
  ...(props.label ? { labels: [props.label] } : {}),
}));

async function fetchStage(stage, page = 1) {
  const response = await ConversationApi.getWorkQueue({
    ...queryParams.value,
    stage,
    page,
  });
  return response.data.stages[stage];
}

async function refresh({ force = false } = {}) {
  if (loading.value && !force) {
    pendingRefresh = true;
    return;
  }
  if (force) {
    pendingRefresh = false;
    if (scheduledRefreshTimer) {
      window.clearTimeout(scheduledRefreshTimer);
      scheduledRefreshTimer = undefined;
    }
  }
  requestId += 1;
  const currentRequest = requestId;
  lastRefreshAt = Date.now();
  loading.value = true;
  try {
    const response = await ConversationApi.getWorkQueue(queryParams.value);
    const firstPages = response.data.stages;
    const stages = await Promise.all(
      stageNames.map(async stage => {
        const pageCount = groups.value[stage]?.page || 1;
        const extraPages = await Promise.all(
          Array.from({ length: pageCount - 1 }, (_, index) =>
            fetchStage(stage, index + 2)
          )
        );
        return {
          ...firstPages[stage],
          page: pageCount,
          conversations: [firstPages[stage], ...extraPages].flatMap(
            page => page.conversations
          ),
        };
      })
    );
    if (currentRequest !== requestId) return;
    groups.value = Object.fromEntries(
      stageNames.map((name, index) => [name, stages[index]])
    );
  } catch {
    if (currentRequest === requestId) useAlert(t('CHAT_LIST.FETCH_ERROR'));
  } finally {
    if (currentRequest === requestId) {
      loading.value = false;
      if (pendingRefresh) {
        pendingRefresh = false;
        // The scheduler coalesces events received while this request was in flight.
        // eslint-disable-next-line no-use-before-define
        scheduleRefresh();
      }
    }
  }
}

function scheduleRefresh() {
  if (!isMounted || scheduledRefreshTimer) return;
  const delay = Math.max(
    0,
    MIN_REFRESH_INTERVAL - (Date.now() - lastRefreshAt)
  );
  scheduledRefreshTimer = window.setTimeout(() => {
    scheduledRefreshTimer = undefined;
    refresh();
  }, delay);
}

async function loadMore(stage) {
  const current = groups.value[stage];
  if (
    !current ||
    loadingMore.value[stage] ||
    current.conversations.length >= current.count
  )
    return;
  loadingMore.value = { ...loadingMore.value, [stage]: true };
  try {
    const next = await fetchStage(stage, current.page + 1);
    groups.value = {
      ...groups.value,
      [stage]: {
        ...next,
        conversations: [...current.conversations, ...next.conversations],
      },
    };
  } catch {
    useAlert(t('CHAT_LIST.FETCH_ERROR'));
  } finally {
    loadingMore.value = { ...loadingMore.value, [stage]: false };
  }
}

watch(queryParams, () => {
  groups.value = {};
  refresh({ force: true });
});
watch(groups, value => {
  emit(
    'loaded',
    stageNames.flatMap(stage => value[stage]?.conversations || [])
  );
});
watch(() => props.refreshKey, scheduleRefresh);
onMounted(() => {
  isMounted = true;
  refresh({ force: true });
  refreshTimer = window.setInterval(scheduleRefresh, 30000);
});
onUnmounted(() => {
  isMounted = false;
  window.clearInterval(refreshTimer);
  window.clearTimeout(scheduledRefreshTimer);
});
</script>

<template>
  <div
    ref="listRef"
    class="flex-1 min-h-0 overflow-y-auto conversations-list"
    :class="{ '!overflow-hidden': isContextMenuOpen }"
  >
    <div
      v-if="loading && !Object.keys(groups).length"
      class="flex justify-center my-4"
    >
      <Spinner class="text-n-brand" />
    </div>
    <div class="conversations-list">
      <section v-for="stage in stageNames" :key="stage">
        <div
          class="flex items-center gap-2 px-4 py-2 border-b border-n-weak"
          :class="stageColors[stage].background"
        >
          <span
            class="w-1 h-4 rounded-full"
            :class="stageColors[stage].marker"
          />
          <h3 class="text-sm font-semibold" :class="stageColors[stage].text">
            {{ stageLabels[stage] }}
            <span>{{ groups[stage]?.count || 0 }}</span>
          </h3>
        </div>
        <ConversationItem
          v-for="conversation in groups[stage]?.conversations || []"
          :key="conversation.id"
          :source="conversation"
          :label="label"
          :team-id="teamId"
          :show-assignee="showAssignee"
          :show-expanded="showExpandedCards"
          :stage-indicator-class="stageColors[stage].marker"
          :stage-indicator-label="stageLabels[stage]"
        />
        <button
          v-if="
            groups[stage] &&
            groups[stage].conversations.length < groups[stage].count
          "
          type="button"
          :disabled="loadingMore[stage]"
          class="w-full py-2 text-sm text-n-brand hover:bg-n-surface-2"
          @click="loadMore(stage)"
        >
          {{ $t('CHAT_LIST.LOAD_MORE_CONVERSATIONS') }}
        </button>
      </section>
    </div>
  </div>
</template>
